import React from "react";
import { Scissors, Monitor, Sparkles, Zap, Sprout, Coins, ArrowRight } from "lucide-react";

const Stitch = ({ className = "", color = "#2E7D32" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const programs = [
  {
    icon: Scissors,
    title: "Tailoring & Fashion Design",
    description: "Hand and machine stitching, pattern-making, and garment finishing for the ready-made and boutique trade.",
    duration: "6 Months",
    level: "Beginner",
  },
  {
    icon: Monitor,
    title: "Computer Literacy",
    description: "Typing, office software, and basic digital services — the entry ticket to today's job market.",
    duration: "3 Months",
    level: "Beginner",
  },
  {
    icon: Sparkles,
    title: "Beautician & Wellness",
    description: "Skin, hair, and salon services training built around real client work and hygiene standards.",
    duration: "4 Months",
    level: "Beginner",
  },
  {
    icon: Zap,
    title: "Electrical & Wiring",
    description: "Domestic wiring, safety codes, and appliance repair — hands-on, tool-in-hand instruction.",
    duration: "6 Months",
    level: "Intermediate",
  },
  {
    icon: Sprout,
    title: "Organic Farming & Agri-Skills",
    description: "Composting, crop rotation, and organic certification basics for smallholder farmers.",
    duration: "3 Months",
    level: "Beginner",
  },
  {
    icon: Coins,
    title: "Entrepreneurship & SHG Finance",
    description: "Bookkeeping, microfinance, and group-lending literacy for Self Help Group members.",
    duration: "2 Months",
    level: "All Levels",
  },
];

const Programs = () => {
  return (
    <section id="programs" className="py-24 bg-[#F2ECDD] scroll-mt-20">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
            Training Programs
          </span>
          <h2 className="mbf-display mt-4 text-4xl font-semibold text-[#0A355C] leading-tight tracking-tight">
            Where Skill Becomes Livelihood
          </h2>
          <Stitch className="w-24 h-2 mx-auto mt-5" color="#2E7D32" />
          <p className="mbf-body mt-6 text-[#514a3d] leading-8">
            Every course runs like an apprenticeship — short, practical, and
            built around a trade our trainees can put to work immediately.
          </p>
        </div>

        {/* Programs Grid — workshop ticket-stub cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map(({ icon: Icon, title, description, duration, level }, i) => (
            <div
              key={i}
              className="group relative flex bg-white border border-[#0A355C]/10 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#0A355C]/5 transition-all duration-300"
            >
              {/* Main body */}
              <div className="flex-1 p-6">
                <div className="w-11 h-11 rounded-full border-2 border-[#0A355C] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#0A355C]">
                  <Icon className="w-5 h-5 text-[#0A355C] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="mbf-display mt-5 text-[18px] font-semibold text-[#0A355C] leading-snug">
                  {title}
                </h3>
                <p className="mbf-body mt-2.5 text-[13.5px] text-[#514a3d] leading-relaxed">
                  {description}
                </p>
                <a
                  href="#contact"
                  className="mbf-body mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#2E7D32]"
                >
                  Enrol interest
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Perforated ticket stub */}
              <div className="relative w-[92px] shrink-0 flex flex-col items-center justify-center bg-[#F2ECDD]/70 border-l-2 border-dashed border-[#0A355C]/20 px-2 py-6">
                <span className="absolute -top-[7px] left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#F2ECDD]" />
                <span className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-[#F2ECDD]" />
                <span className="mbf-mono text-[11px] font-medium text-[#0A355C] text-center leading-tight">
                  {duration}
                </span>
                <span className="mbf-mono text-[9.5px] uppercase tracking-wide text-[#2E7D32] mt-2 text-center">
                  {level}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <a
            href="#programs"
            className="mbf-body inline-flex items-center gap-2 bg-[#0A355C] hover:bg-[#082440] text-white px-8 py-4 font-semibold text-xs uppercase tracking-wider transition shadow-md"
          >
            Explore All Programs &amp; Details
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Programs;